import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Loader2, LogOut } from "lucide-react";
import SEO from "@/components/SEO";
import { admin, formatError } from "@/lib/growthApi";

export const CATEGORY_STYLE = {
  "Priority Opportunity": "text-gold border-gold/50",
  "Developing Opportunity": "text-electric border-electric/50",
  "Early Stage": "text-slate-400 border-white/20",
};

export function Tag({ children, className = "" }) {
  return <span className={`inline-block border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em] ${className}`}>{children}</span>;
}

export function useAdmin() {
  const [state, setState] = useState(null);
  useEffect(() => {
    if (!localStorage.getItem("thrive_admin_token")) { setState(false); return; }
    admin.me().then(() => setState(true)).catch(() => { localStorage.removeItem("thrive_admin_token"); setState(false); });
  }, []);
  return state;
}

export function AdminShell({ title, children }) {
  const ok = useAdmin();
  const nav = useNavigate();
  if (ok === null) return <div className="flex min-h-screen items-center justify-center pt-24"><Loader2 className="animate-spin text-gold" /></div>;
  if (ok === false) return <Navigate to="/admin/login" replace />;
  return (
    <div className="pt-28 sm:pt-32 pb-20" data-testid="admin-shell">
      <SEO title={`${title} | Thrive Admin`} description="Thrive Business Development admin." />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-white/10 pb-6">
          <div className="flex flex-wrap items-center gap-6">
            <p className="eyebrow">Thrive Admin</p>
            <nav className="flex gap-5 font-mono text-[11px] uppercase tracking-[0.2em]">
              <Link to="/admin" data-testid="admin-nav-overview" className="text-slate-400 transition-colors hover:text-gold">Overview</Link>
              <Link to="/admin/leads" data-testid="admin-nav-leads" className="text-slate-400 transition-colors hover:text-gold">Leads</Link>
              <Link to="/admin/notifications" data-testid="admin-nav-notifications" className="text-slate-400 transition-colors hover:text-gold">Notifications</Link>
              <Link to="/admin/clients" data-testid="admin-nav-clients" className="text-slate-400 transition-colors hover:text-gold">Clients</Link>
            </nav>
          </div>
          <button type="button" data-testid="admin-logout-button" onClick={() => { localStorage.removeItem("thrive_admin_token"); nav("/admin/login"); }} className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500 transition-colors hover:text-gold"><LogOut size={12} /> Sign out</button>
        </div>
        <h1 className="mt-8 font-display text-2xl sm:text-3xl font-bold tracking-[-0.03em] text-white">{title}</h1>
        <div className="mt-8">{children}</div>
      </div>
    </div>
  );
}

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { const d = await admin.login(email, password); localStorage.setItem("thrive_admin_token", d.token); nav("/admin"); }
    catch (ex) { setErr(formatError(ex)); } finally { setBusy(false); }
  };
  const INPUT = "w-full border border-white/15 bg-transparent px-4 py-3 text-sm text-white outline-none transition-colors focus:border-gold/60";
  return (
    <div className="flex min-h-[80vh] items-center justify-center px-4 pt-24" data-testid="admin-login-page">
      <SEO title="Admin Sign In | Thrive" description="Thrive admin sign in." />
      <form onSubmit={submit} className="w-full max-w-sm border border-white/10 bg-navy-light/30 p-8">
        <p className="eyebrow mb-4">Thrive Admin</p>
        <h1 className="font-display text-2xl font-bold tracking-[-0.03em] text-white">Sign in</h1>
        <div className="mt-8 space-y-5">
          <div><label htmlFor="admin-email" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">Email</label><input id="admin-email" data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={INPUT} /></div>
          <div><label htmlFor="admin-password" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">Password</label><input id="admin-password" data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={INPUT} /></div>
        </div>
        {err && <p className="mt-4 text-sm text-red-400" data-testid="admin-login-error">{err}</p>}
        <button type="submit" disabled={busy} data-testid="admin-login-button" className="mt-8 inline-flex w-full items-center justify-center gap-2 bg-gold px-6 py-3.5 text-[12px] font-semibold uppercase tracking-[0.18em] text-navy transition-all hover:bg-gold-glow disabled:opacity-60">{busy ? <Loader2 size={14} className="animate-spin" /> : null} Sign in</button>
      </form>
    </div>
  );
}
